import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Plus, Edit2, Trash2, Users, Star } from 'lucide-react';
import { testimonialsAPI, uploadAPI } from '../../utils/api';
import { toast } from 'sonner';

const AdminTestimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    text: '',
    rating: 5,
    avatar: '',
    is_active: true
  });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await testimonialsAPI.getAllAdmin();
      setTestimonials(response.data);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
      toast.error('Ошибка загрузки отзывов');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      company: '',
      text: '',
      rating: 5,
      avatar: '',
      is_active: true
    });
    setEditingTestimonial(null);
  };

  const openDialog = (testimonial = null) => {
    if (testimonial) {
      setFormData({
        name: testimonial.name,
        company: testimonial.company,
        text: testimonial.text,
        rating: testimonial.rating,
        avatar: testimonial.avatar,
        is_active: testimonial.is_active
      });
      setEditingTestimonial(testimonial);
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAvatarUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Пожалуйста, выберите изображение');
      return;
    }

    setUploading(true);
    try {
      const response = await uploadAPI.uploadFile(file);
      const avatarUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      
      setFormData(prev => ({
        ...prev,
        avatar: avatarUrl
      }));
      
      toast.success('Аватар успешно загружен');
    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast.error('Ошибка загрузки аватара');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingTestimonial) {
        await testimonialsAPI.update(editingTestimonial.id, formData);
        toast.success('Отзыв успешно обновлён');
      } else {
        await testimonialsAPI.create(formData);
        toast.success('Отзыв успешно создан');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchTestimonials();
    } catch (error) {
      console.error('Error saving testimonial:', error);
      toast.error('Ошибка сохранения отзыва');
    }
  };

  const handleDelete = async (testimonialId) => {
    if (confirm('Вы уверены, что хотите удалить этот отзыв?')) {
      try {
        await testimonialsAPI.delete(testimonialId);
        toast.success('Отзыв успешно удалён');
        fetchTestimonials();
      } catch (error) {
        console.error('Error deleting testimonial:', error);
        toast.error('Ошибка удаления отзыва');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">Загружается...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Управление отзывами</h1>
          <p className="text-gray-400">Создание и редактирование отзывов клиентов</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              onClick={() => openDialog()}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="add-testimonial-btn"
            >
              <Plus className="mr-2 h-4 w-4" />
              Добавить отзыв
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editingTestimonial ? 'Редактировать отзыв' : 'Создать новый отзыв'}
              </DialogTitle>
              <DialogDescription className="text-gray-400">
                Заполните информацию об отзыве клиента
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-gray-300">Имя клиента</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="Иван Иванов"
                    required
                    data-testid="testimonial-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="company" className="text-gray-300">Компания (необязательно)</Label>
                  <Input
                    id="company"
                    value={formData.company}
                    onChange={(e) => handleInputChange('company', e.target.value)}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="ООО Компания"
                    data-testid="testimonial-company-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="text" className="text-gray-300">Текст отзыва</Label>
                <Textarea
                  id="text"
                  value={formData.text}
                  onChange={(e) => handleInputChange('text', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white min-h-[120px]"
                  placeholder="Отличный бот! Очень помогает в работе..."
                  required
                  data-testid="testimonial-text-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="rating" className="text-gray-300">Рейтинг (1-5 звёзд)</Label>
                  <Input
                    id="rating"
                    type="number"
                    min="1"
                    max="5"
                    value={formData.rating}
                    onChange={(e) => handleInputChange('rating', parseInt(e.target.value))}
                    className="bg-gray-700 border-gray-600 text-white"
                    required
                    data-testid="testimonial-rating-input"
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="is_active"
                      checked={formData.is_active}
                      onCheckedChange={(checked) => handleInputChange('is_active', checked)}
                      data-testid="testimonial-active-switch"
                    />
                    <Label htmlFor="is_active" className="text-gray-300">Активный</Label>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300">Аватар клиента</Label>
                {formData.avatar && (
                  <div className="mb-2">
                    <img 
                      src={formData.avatar} 
                      alt="Avatar" 
                      className="w-16 h-16 object-cover rounded-full"
                    />
                  </div>
                )}
                <div className="flex gap-2">
                  <Input
                    value={formData.avatar}
                    onChange={(e) => handleInputChange('avatar', e.target.value)}
                    className="bg-gray-700 border-gray-600 text-white flex-1"
                    placeholder="URL аватара"
                    data-testid="testimonial-avatar-input"
                  />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarUpload}
                    className="hidden"
                    id="avatar-upload"
                    disabled={uploading}
                  />
                  <Button
                    type="button"
                    onClick={() => document.getElementById('avatar-upload').click()}
                    disabled={uploading}
                    variant="outline"
                    className="border-gray-600"
                  >
                    {uploading ? 'Загрузка...' : 'Выбрать файл'}
                  </Button>
                </div>
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="border-gray-600 text-gray-300"
                >
                  Отмена
                </Button>
                <Button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="save-testimonial-btn"
                >
                  {editingTestimonial ? 'Обновить' : 'Создать'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Testimonials Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {testimonials.map((testimonial, index) => (
          <motion.div
            key={testimonial.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: index * 0.1 }}
          >
            <Card className="bg-gray-800 border-gray-700 h-full">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gray-700 rounded-full flex items-center justify-center overflow-hidden">
                      {testimonial.avatar ? (
                        <img src={testimonial.avatar} alt={testimonial.name} className="w-full h-full object-cover" />
                      ) : (
                        <Users className="h-6 w-6 text-gray-400" />
                      )}
                    </div>
                    <div>
                      <div className="font-semibold text-white">{testimonial.name}</div>
                      {testimonial.company && (
                        <div className="text-sm text-gray-400">{testimonial.company}</div>
                      )}
                    </div>
                  </div>
                  <Badge className={testimonial.is_active ? 'bg-green-600' : 'bg-gray-600'}>
                    {testimonial.is_active ? 'Активен' : 'Неактивен'}
                  </Badge>
                </div>
                
                <div className="flex items-center mt-2">
                  {[...Array(5)].map((_, i) => (
                    <Star 
                      key={i} 
                      className={`h-4 w-4 ${
                        i < testimonial.rating ? 'text-yellow-400 fill-current' : 'text-gray-600'
                      }`} 
                    />
                  ))}
                </div>
              </CardHeader>
              
              <CardContent className="flex-1">
                <p className="text-gray-300 mb-4">{testimonial.text}</p>
                
                <div className="flex justify-end space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openDialog(testimonial)}
                    className="text-blue-400 hover:text-blue-300"
                    data-testid={`edit-testimonial-${testimonial.id}`}
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(testimonial.id)}
                    className="text-red-400 hover:text-red-300"
                    data-testid={`delete-testimonial-${testimonial.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {testimonials.length === 0 && (
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="text-center py-12">
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-400 mb-4">Отзывы пока не добавлены</p>
            <Button 
              onClick={() => openDialog()}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Добавить первый отзыв
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminTestimonials;